import axiosInstance from '../libs/axiosInstance';

// Fetch quiz and format response
export const adminLecturerGetQuiz = async (quizId) => {
  const response = await axiosInstance.get(`/question_bank/quizzes/${quizId}/`);
  return response.data;
};

export const getLecturerQuiz = async () => {
  const response = await axiosInstance.get('question_bank/lecturer-quizzes/')
  return response.data
}

export const getAdminQuiz = async () => {
  const response = await axiosInstance.get('question_bank/admin-quizzes/')
  return response.data
}

export const createQuiz = async (quizData) => {
  const response = await axiosInstance.post('/question_bank/quizzes/', quizData);
  return response.data;
};

export const updateQuiz = async (quizId, quizData) => {
  const response = await axiosInstance.put(`/question_bank/quizzes/${quizId}/`, quizData);
  return response.data;
};

export const patchQuiz = async (quizId, quizData) => {
  const response = await axiosInstance.patch(`/question_bank/quizzes/${quizId}/`, quizData);
  return response.data;
};

export const deleteQuiz = async (quizId) => {
  await axiosInstance.delete(`/question_bank/quizzes/${quizId}/`);
};


// Submit quiz answers
export const submitQuizAnswers = async (sessionId, answers) => {
  const response = await axiosInstance.post(`/quiz-session/${sessionId}/submit/`, answers);
  return response.data;
};

// Submit a single question answer (save-as-you-go)
export const submitQuestionAnswer = async (sessionId, answer) => {
  const payload = { ...answer };
  delete payload.type; // strip internal type field — backend doesn't need it
  const response = await axiosInstance.post(`/quiz-session/${sessionId}/answer/`, payload);
  return response.data;
};

// Get quiz session results
export const getQuizResults = async (sessionId) => {
  const response = await axiosInstance.get(`/quiz-session/${sessionId}/result/`);
  return response.data;
};

// Helper to build the question object with either quiz or question_bank
function buildQuestionPayload(questionData, { quizId = null, questionBankId = null }) {
  const question = {
    text: questionData.text,
    order: questionData.order || 0,
  };
  if (quizId) question.quiz = quizId;
  if (questionBankId) question.question_bank = questionBankId;
  if (questionData.image) question.image = questionData.image;
  return question;
}

function buildOptionPayload(option) {
  return {
    ...(option.id && { id: option.id }), // include id if present (for update)
    text: option.text,
    is_correct: option.is_correct || false,
    explanation: option.explanation, // fixed typo: explanaion -> explanation
  };
}

// Update theory question
export const updateTheoryQuestion = async (questionId, questionData, { quizId = null, questionBankId = null }) => {
  const payload = {
    question: buildQuestionPayload(questionData, { quizId, questionBankId }),
    vetting_method: questionData.vetting_method || 'onetime',
    answer: questionData.answer,
  };
  const response = await axiosInstance.patch(`/question_bank/questions/theory/${questionId}/`, payload);
  return response.data;
};

// Update objective question
export const updateObjectiveQuestion = async (questionId, questionData, { quizId = null, questionBankId = null }) => {
  const payload = {
    question: buildQuestionPayload(questionData, { quizId, questionBankId }),
    options: questionData.options.map(buildOptionPayload),
    correct_explanation: questionData.correct_explanation,
    wrong_explanation: questionData.wrong_explanation,
  };
  const response = await axiosInstance.patch(`/question_bank/questions/objective/${questionId}/`, payload);
  return response.data;
};

export const updateMatchingQuestion = async (questionId, questionData, { quizId = null, questionBankId = null }) => {
  const payload = {
    question: buildQuestionPayload(questionData, { quizId, questionBankId }),
    matching_pairs: questionData.matching_pair.map((pair) => ({
      id: pair.id,
      left: pair.left,
      right: pair.right,
    })),
  };
  const response = await axiosInstance.patch(`/question_bank/questions/matching/${questionId}/`, payload);
  return response.data;
}

// Update multiple select question
export const updateMultipleSelectQuestion = async (questionId, questionData, { quizId = null, questionBankId = null }) => {
  const payload = {
    question: buildQuestionPayload(questionData, { quizId, questionBankId }),
    options: questionData.options.map(buildOptionPayload),
    correct_explanation: questionData.correct_explanation,
    wrong_explanation: questionData.wrong_explanation,
  };
  const response = await axiosInstance.patch(`/question_bank/questions/multiple_select/${questionId}/`, payload);
  return response.data;
};

// Delete question (caller must pass correctly-formatted type segment)
export const deleteQuestion = async (questionId, questionType, { from_bank = null, from_quiz = null } = {}) => {
  const params = {};
  if (from_bank) params.from_bank = from_bank;
  if (from_quiz) params.from_quiz = from_quiz;
  await axiosInstance.delete(`/question_bank/questions/${questionType}/${questionId}/`, { params });
};


// Create theory question
export const createTheoryQuestion = async (questionData, { quizId = null, questionBankId = null }) => {
  const payload = {
    question: buildQuestionPayload(questionData, { quizId, questionBankId }),
    vetting_method: questionData.vetting_method || 'onetime',
    answer: questionData.answer,
  };
  const response = await axiosInstance.post(`/question_bank/questions/theory/`, payload);
  return response.data;
};

// Create objective question
export const createObjectiveQuestion = async (questionData, { quizId = null, questionBankId = null }) => {
  const payload = {
    question: buildQuestionPayload(questionData, { quizId, questionBankId }),
    options: questionData.options.map(buildOptionPayload),
    correct_explanation: questionData.correct_explanation,
    wrong_explanation: questionData.wrong_explanation,
  };
  const response = await axiosInstance.post(`/question_bank/questions/objective/`, payload);
  return response.data;
};

// Create matching question
export const createMatchingQuestion = async (questionData, { quizId = null, questionBankId = null }) => {
  const payload = {
    question: buildQuestionPayload(questionData, { quizId, questionBankId }),
    matching_pairs: questionData.matching_pair.map((pair) => ({
      left: pair.left,
      right: pair.right,
    })),
  };
  const response = await axiosInstance.post(`/question_bank/questions/matching/`, payload);
  return response.data;
};

// Create multiple select question
export const createMultipleSelectQuestion = async (questionData, { quizId = null, questionBankId = null }) => {
  const payload = {
    question: buildQuestionPayload(questionData, { quizId, questionBankId }),
    options: questionData.options.map(buildOptionPayload),
    correct_explanation: questionData.correct_explanation,
    wrong_explanation: questionData.wrong_explanation,
  };
  const response = await axiosInstance.post(`/question_bank/questions/multiple_select/`, payload);
  return response.data;
};

export const bulkDeleteQuestions = async ({ quizId = null, questionBankId = null }) => {
  const params = {};
  if (quizId) params.quiz_id = quizId;
  if (questionBankId) params.question_bank_id = questionBankId;
  return axiosInstance.delete('/question_bank/questions/bulk-delete/', { params });
};

export const retreiveCourseQuizzesSessions = async (courseId, quiz_type) => {
  const response = await axiosInstance.get(
    `/quiz-session/admin/sessions/by-course-quiz-type?course_id=${courseId}&quiz_type=${quiz_type}`,
    {
      timeout: 60000, // 60 seconds timeout for large data
    }
  );
  return response.data;
}

// Resolve/Answer a verification challenge question during quiz session
export const resolveChallengeQuestion = async (sessionId, eventId, action, responseText = null) => {
  const payload = { action };
  if (action === 'answer' && responseText !== null) {
    payload.response = responseText;
  }
  const response = await axiosInstance.post(`/quiz-session/${sessionId}/challenge/${eventId}/resolve/`, payload);
  return response.data;
};

// Trigger/Fetch active verification challenge question during quiz session
export const triggerChallengeQuestion = async (sessionId) => {
  const response = await axiosInstance.post(`/quiz-session/${sessionId}/challenge/`);
  return response.data;
};

// Fetch the next quiz for a topic
export const getNextQuiz = async (topicId) => {
  const response = await axiosInstance.get(`/question_bank/public/topics/${topicId}/next-quiz/`);
  return response.data;
};